import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import RouteAttributes from "@albatrosdeveloper/ave-models-package/src/schemas/routing/route/route.entity";
import { ICreateRoute } from "./interfaces/create-route.interface";
import { IUpdateRoute } from "./interfaces/update-route.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
import { IFilters } from "../../filters/interfaces/filterInterface";

const apiUrl = env.API_ROUTING_URL;
const moduleName = "route";

@Injectable({
  providedIn: "root",
})
export class RouteService extends AbstractService<
  RouteAttributes,
  ICreateRoute,
  IUpdateRoute
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<RouteAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }
  findByFilter(filter: CombinedFilter<RouteAttributes>, options?: RequestOptions) {
      const API_URL: string =
        apiUrl +
        `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
        const headers: Record<string, any> = {};
        // see order-customer-export for more info about this header
        // ctx: stands for context
        if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
        return this.http.get<IPaginationResult<RouteAttributes>>(API_URL, { headers });
    }

  /**
   * Obtiene territorios con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Tres objetos (OR lógico) - busca en 'id', 'name', 'externalKeys.code'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param dataToFilter Filters to apply
   * @returns Paginated data
   *
   * Search fields: id, name, externalKeys.code
   * Array filters: status
   * Single filters: companyId
   */
  findAllWithCount(
    options: IPagination<RouteAttributes>,
    dataToFilter: IFilters
  ): Observable<IPaginationResult<RouteAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtro de compañía, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<RouteAttributes>[] => {
      const deletedFilter: FilterFn<RouteAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        dataToFilter?.companyId
          ? andWhere('company.id' as keyof RouteAttributes, Ops.eq(dataToFilter.companyId))
          : noop(),
        dataToFilter?.status && dataToFilter.status.length > 0
          ? andWhere('statusId', Ops.in(...dataToFilter.status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por ID (o solo condiciones si no hay búsqueda)
    const searchByIdQueryObject: FilterFn<RouteAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<RouteAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // Objeto 3: Condiciones base + búsqueda por EXTERNAL KEYS CODE (solo si hay búsqueda)
    const searchByExternalKeysQueryObject: FilterFn<RouteAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("externalKeys.code" as keyof RouteAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<RouteAttributes>(
      // Expandimos los tres objetos en el buildQuery
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,
      ...searchByExternalKeysQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<RouteAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(companyId?: string): Observable<IRequestResult<RouteAttributes[]>> {
    const query = buildQuery<RouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      companyId ? andWhere("company._id" as keyof RouteAttributes, Ops.eq(companyId)) : noop(),
      select(["id", "externalKeys", "name","_id"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<RouteAttributes[]>>(API_URL);
  }

  findToComboBoxById(companyId?: number): Observable<IRequestResult<RouteAttributes[]>> {
    const query = buildQuery<RouteAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      companyId ? andWhere("company.id" as keyof RouteAttributes, Ops.eq(companyId)) : noop(),
      select(["id", "externalKeys", "name","_id"]),
      orderBy("id", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<RouteAttributes[]>>(API_URL);
  }

  /**
   * Obtiene territorios paginados filtrados por múltiples companyIds.
   *
   * @param options Opciones de paginación (currentPage, limitPerPage, search, sort, sortDirection)
   * @param companyIds Array de IDs numéricos de companies
   * @returns Observable con datos paginados
   */
  findAllWithCountByCompanyIds(
    options: IPagination<RouteAttributes>,
    companyIds: number[]
  ): Observable<IPaginationResult<RouteAttributes>> {
    const hasSearch = !!options.search;

    /**
     * Construye filtros base: _deleted=false, statusId=ACTIVE, company.id IN companyIds
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<RouteAttributes>[] => {
      const deletedFilter: FilterFn<RouteAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))
        : where('_deleted', Ops.eq(false));

      return [
        deletedFilter,
        andWhere('statusId', Ops.eq(StatusEnum.ACTIVE)),
        companyIds.length > 0
          ? andWhere('company.id' as keyof RouteAttributes, Ops.in(...companyIds))
          : noop(),
      ];
    };

    // Búsqueda en id, name, externalKeys.code (OR lógico)
    const searchByIdQueryObject: FilterFn<RouteAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),
      hasSearch ? andWhere("id", Ops.like(options.search!, "i")) : noop()
    ];

    const searchByNameQueryObject: FilterFn<RouteAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    const searchByCodeQueryObject: FilterFn<RouteAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),
      andWhere("externalKeys.code" as keyof RouteAttributes, Ops.like(options.search!, "i"))
    ] : [];

    const query = buildQuery<RouteAttributes>(
      ...searchByIdQueryObject,
      ...searchByNameQueryObject,
      ...searchByCodeQueryObject,
      orderBy(options.sort ?? "name", options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc),
      page(options.currentPage ?? 1, options.limitPerPage ?? 10)
    );

    const API_URL = `${apiUrl}api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<RouteAttributes>>(API_URL);
  }
}
